import { createReducer } from '@reduxjs/toolkit';

import { SortAction } from '../../enums';
import { getSenderInfo } from '../../helpers';

import * as MailboxActions from './actions';
import { Constants } from './constants';
import { IMailboxState } from './interfaces';

const initialMailboxState: IMailboxState = {
    selectedFolder: {
        id: undefined,
        name: undefined,
        parentId: undefined,
    },
    mailbox: {
        id: undefined,
        ownerType: undefined,
        ownerName: undefined,
        name: undefined,
        organisationId: undefined,
        public: undefined,
        exempt: undefined,
        access: undefined,
    },
    folderList: [],
    documentList: [],
    filteredDocumentList: [],
    selectedDocuments: [],
    foldersUpdateAction: null,
    unreadCounter: null,
    searchValue: '',
    searchInputText: '',
    sortOrder: SortAction.SORT_BY_DATE_NEWEST_TOP,
    filterBy: null,
    searchEnabledOutsideMailbox: false,
    currentShownDocumentsCount: undefined,
    editNoteDocument: undefined,
    isMailboxMounted: false,
    selectedFolderAction: null,
    showBatchError: false,
    waitForUpdate: false,
    clickForMoveUpdatingState: false,
    userRoles: [],
    selectSenderFolder: {
        folderInfo: null,
        originalRecords: null,
        folderRecords: null,
    },
    selectedSender: null,
    isSenderClicked: false,
    isFromSingleMessage: true,
    isGreenlandSubscription: false,
    isReadUnreadAction: false,
};

export const mailboxReducer = createReducer(initialMailboxState, {
    [Constants.MAILBOX_SELECT]: (draftState, action: ReturnType<typeof MailboxActions.selectMailbox>) => {
        draftState.mailbox = action.payload.mailbox;
        draftState.documentList = [];
        draftState.selectSenderFolder.originalRecords = [];
        draftState.selectedSender = null;
    },

    [Constants.MAILBOX_FOLDER_SELECT]: (draftState, action: ReturnType<typeof MailboxActions.selectFolder>) => {
        draftState.selectedFolder = action.payload.folder;
        if (draftState.isFromSingleMessage) {
            draftState.documentList =
                draftState.selectedSender == null ? [] : draftState.selectSenderFolder.originalRecords;
            draftState.selectedDocuments = [];
        }
        draftState.searchValue = draftState.searchEnabledOutsideMailbox ? draftState.searchValue : '';
        draftState.searchEnabledOutsideMailbox = false;
        draftState.filterBy = null;
    },
    [Constants.MAILBOX_DOCUMENT_SELECT_SINGLEMESSAGE]: (
        draftState,
        action: ReturnType<typeof MailboxActions.selectbysinglemessage>
    ) => {
        draftState.isFromSingleMessage = action.payload.show;
    },
    [Constants.MAILBOX_SENDER_SELECT]: (
        draftState,
        action: ReturnType<typeof MailboxActions.setSelectSenderFolder>
    ) => {
        if (action.payload) {
            if (draftState.isSenderClicked && !draftState.clickForMoveUpdatingState) {
                // const x = draftState.selectSenderFolder.originalRecords.concat(action.payload.folder.originalRecords);
                const seen = new Map();

                // Merge the arrays without coping duplicate records
                const x = draftState.selectSenderFolder.originalRecords
                    .concat(action.payload.folder.originalRecords)
                    .filter((obj) => {
                        const key = JSON.stringify(obj);
                        if (seen.has(key)) {
                            return false;
                        }
                        seen.set(key, true);
                        return true;
                    });
                const y = [
                    ...new Set(
                        x
                            .filter((item) => draftState.selectedSender?.id === item.senderOrganisationId)
                            .map((item) => item.id)
                    ),
                ];
                draftState.selectSenderFolder = {
                    ...draftState.selectSenderFolder,
                    originalRecords: x.filter((item: any) => y.indexOf(item.id) > -1),
                };
            } else {
                draftState.selectSenderFolder = action.payload.folder;
            }
        }
    },

    [Constants.MAILBOX_FOLDER_LIST_SET]: (draftState, action: ReturnType<typeof MailboxActions.setFolderList>) => {
        draftState.folderList = action.payload.folders;
    },

    [Constants.MAILBOX_DOCUMENTS_SET]: (draftState, action: ReturnType<typeof MailboxActions.setDocumentList>) => {
        if (!draftState.searchValue) {
            const x = draftState.documentList.concat(action.payload.documentsList);
            const y = [...new Set(x.map((item) => item.id))];
            draftState.documentList = x.filter((item) => y.indexOf(item.id) > -1);
        } else {
            draftState.documentList = action.payload.documentsList;
        }
        if (draftState.selectedSender != null) {
            draftState.filteredDocumentList = draftState.documentList.filter(
                (item) => item?.senderName?.toLowerCase() == draftState.selectedSender?.name?.toLowerCase()
            );
        }
    },

    [Constants.MAILBOX_DOCUMENT_REPLACE]: (draftState, action: ReturnType<typeof MailboxActions.replaceDocument>) => {
        const indexToReplace = draftState.documentList.findIndex((doc) => doc.id === action.payload.document.id);

        if (indexToReplace < 0) {
            return;
        }

        draftState.documentList[indexToReplace] = action.payload.document;
    },

    [Constants.MAILBOX_DOCUMENTS_CURRENT_SHOWN_COUNT_SET]: (
        draftState,
        action: ReturnType<typeof MailboxActions.setCurrentShownDocumentsCount>
    ) => {
        draftState.currentShownDocumentsCount = action.payload.currentShownCount;
    },

    [Constants.MAILBOX_DOCUMENTS_CLEAN]: (draftState) => {
        draftState.documentList = [];
    },

    [Constants.MAILBOX_DOCUMENTS_SELECT]: (
        draftState,
        action: ReturnType<typeof MailboxActions.setSelectedDocuments>
    ) => {
        draftState.selectedDocuments = action.payload.selectedDocuments;
    },

    [Constants.MAILBOX_FOLDER_UPDATE_ACTION]: (
        draftState,
        action: ReturnType<typeof MailboxActions.setFolderListUpdatedAction>
    ) => {
        draftState.foldersUpdateAction = action.payload.foldersUpdateAction;
    },

    [Constants.MAILBOX_DOCUMENT_UPDATE]: (draftState, action: ReturnType<typeof MailboxActions.updateDocument>) => {
        const { document } = action.payload;

        const documentIndex = draftState.documentList.findIndex(({ id }) => id === document.id);
        const documentIndexOriginalRecords = draftState?.selectSenderFolder?.originalRecords?.findIndex(
            ({ id }) => id === document.id
        );

        if (documentIndex < 0) {
            return;
        }

        draftState.documentList[documentIndex] = { ...draftState.documentList[documentIndex], ...document };
        draftState.selectedDocuments = draftState.selectedDocuments.map((selectedDocument) => {
            const updatedDocument = draftState.documentList.find(({ id }) => id === selectedDocument.id);

            return updatedDocument
                ? {
                      id: selectedDocument.id,
                      version: updatedDocument.version,
                  }
                : selectedDocument;
        });

        if (draftState.isSenderClicked) {
            draftState.selectSenderFolder.originalRecords[documentIndexOriginalRecords] = {
                ...draftState.selectSenderFolder.originalRecords[documentIndexOriginalRecords],
                ...document,
            };
            draftState.filteredDocumentList = draftState.documentList.filter(
                (item) => item?.senderName?.toLowerCase() == draftState.selectedSender?.name?.toLowerCase()
            );

            const result = getSenderInfo(draftState.selectSenderFolder.originalRecords);
            draftState.selectSenderFolder = {
                ...draftState.selectSenderFolder,
                folderRecords: [...result],
            };
        }
    },

    [Constants.MAILBOX_UNREAD_COUNT_SET]: (draftState, action: ReturnType<typeof MailboxActions.setUnreadCounter>) => {
        const { unreadCounter } = action.payload;

        const sortUnreadCounter = unreadCounter.items.filter((item) => item.folderId != 'FlaggedFolder_');

        const unreadCount = sortUnreadCounter.reduce((sum, item) => sum + item._count.id, 0);

        draftState.unreadCounter = { ...unreadCounter, unreadCount: unreadCount };
    },

    [Constants.MAILBOX_DOCUMENT_REMOVE]: (draftState, action: ReturnType<typeof MailboxActions.removeDocument>) => {
        const { document } = action.payload;

        draftState.selectedDocuments = [];
        draftState.documentList = draftState.documentList.filter(({ id }) => id !== document.id);
    },

    [Constants.MAILBOX_FOLDER_UPDATE_NAME]: (
        draftState,
        action: ReturnType<typeof MailboxActions.updateFolderName>
    ) => {
        const { folderId } = action.payload;

        const folderIndex = draftState.folderList.findIndex(({ id }) => id === folderId);

        if (folderIndex < 0) {
            return;
        }

        const updatedFolder = { ...draftState.folderList[folderIndex], name: action.payload.newName };
        updatedFolder.version += 1;

        draftState.folderList[folderIndex] = updatedFolder;
        draftState.selectedFolder = {
            id: updatedFolder.id,
            parentId: updatedFolder.parentId,
            name: updatedFolder.name,
        };
    },

    [Constants.MAILBOX_DOCUMENT_REMOVE_BY_ID]: (
        draftState,
        action: ReturnType<typeof MailboxActions.removeDocumentById>
    ) => {
        draftState.selectedDocuments = [];
        draftState.documentList = draftState.documentList.filter(({ id }) => id !== action.payload.documentId);
    },

    [Constants.MAILBOX_SET_SEARCH_VALUE]: (draftState, action: ReturnType<typeof MailboxActions.setSearchValue>) => {
        draftState.searchValue = action.payload.searchValue;
    },

    [Constants.MAILBOX_LIST_SORTORDER_ACTION]: (draftState, action: ReturnType<typeof MailboxActions.setSortOrder>) => {
        draftState.sortOrder = action.payload.sortOrder;
    },

    [Constants.MAILBOX_LIST_FILTERBY_ACTION]: (draftState, action: ReturnType<typeof MailboxActions.setFilterBy>) => {
        draftState.filterBy = action.payload.filterBy;
    },

    [Constants.MAILBOX_SET_SEARCH_INPUT_TEXT]: (
        draftState,
        action: ReturnType<typeof MailboxActions.setSearchInputText>
    ) => {
        draftState.searchInputText = action.payload.text;
    },

    [Constants.MAILBOX_SET_SEARCH_OUTSIDE_MAILBOX]: (
        draftState,
        action: ReturnType<typeof MailboxActions.setSearchOutsideMailbox>
    ) => {
        draftState.searchEnabledOutsideMailbox = action.payload.enabled;
    },

    [Constants.MAILBOX_DOCUMENT_UNSELECT_BY_ID]: (
        draftState,
        action: ReturnType<typeof MailboxActions.unselectDocumentById>
    ) => {
        draftState.selectedDocuments = draftState.selectedDocuments.filter(
            ({ id }) => id !== action.payload.documentId
        );
    },

    [Constants.MAILBOX_DOCUMENT_UNSELECT_BY_IDS]: (
        draftState,
        action: ReturnType<typeof MailboxActions.unselectDocumentByIds>
    ) => {
        draftState.selectedDocuments = draftState.selectedDocuments.filter(({ id }) => {
            action.payload.documentIds.includes(id);
        });
    },

    [Constants.MAILBOX_DOCUMENT_SELECT_BY_ID]: (
        draftState,
        action: ReturnType<typeof MailboxActions.selectDocumentById>
    ) => {
        const { documentId, version } = action.payload;

        const selectedDocuments = draftState.selectedDocuments.filter(({ id }) => id !== documentId);
        selectedDocuments.push({ id: documentId, version });

        draftState.selectedDocuments = selectedDocuments;
    },

    [Constants.MAILBOX_SET_EDIT_NOTE_DOCUMENT]: (
        draftState,
        action: ReturnType<typeof MailboxActions.setEditNoteDocument>
    ) => {
        draftState.editNoteDocument = action.payload.document;
    },

    [Constants.MAILBOX_SET_IS_MAILBOX_MOUNTED]: (
        draftState,
        action: ReturnType<typeof MailboxActions.setIsMailboxMounted>
    ) => {
        draftState.isMailboxMounted = action.payload.mounted;
    },

    [Constants.MAILBOX_SET_SELECTED_FOLDER_ACTION]: (
        draftState,
        action: ReturnType<typeof MailboxActions.setSelectedFolderAction>
    ) => {
        draftState.selectedFolderAction = action.payload.folderAction;
    },

    [Constants.MAILBOX_DOCUMENTS_REPLACE]: (
        draftState,
        action: ReturnType<typeof MailboxActions.replaceDocumentList>
    ) => {
        const { documentListToReplace } = action.payload;

        draftState.documentList = documentListToReplace;
        draftState.selectedDocuments = draftState.selectedDocuments
            .map((selectedDocument) => {
                const newVersionOfDocument = documentListToReplace.find(({ id }) => id === selectedDocument.id);

                if (!newVersionOfDocument) {
                    return null;
                }

                return {
                    id: selectedDocument.id,
                    version: newVersionOfDocument.version,
                };
            })
            .filter(Boolean);
    },

    [Constants.MAILBOX_SHOW_BATCH_ERROR]: (draftState, action: ReturnType<typeof MailboxActions.showBatchError>) => {
        draftState.showBatchError = action.payload.show;
    },

    [Constants.MAILBOX_DOCUMENTS_DELETE_FROM_LIST]: (
        draftState,
        action: ReturnType<typeof MailboxActions.deleteDocumentsFromList>
    ) => {
        const documentIdsToDelete = new Set(action.payload.documentsToDelete.map(({ id }) => id));

        const updatedDocumentList = draftState.documentList.filter(({ id }) => !documentIdsToDelete.has(id));
        const updatedDocumentListOriginalRecords = draftState.selectSenderFolder.originalRecords.filter(
            ({ id }) => !documentIdsToDelete.has(id)
        );

        const updatedSelectedList = draftState.selectedDocuments.filter(({ id }) => !documentIdsToDelete.has(id));
        draftState.documentList = updatedDocumentList;

        if (draftState.isSenderClicked) {
            draftState.selectSenderFolder.originalRecords = updatedDocumentListOriginalRecords;
            draftState.filteredDocumentList = draftState.documentList.filter(
                (item) => item?.senderName?.toLowerCase() == draftState.selectedSender?.name?.toLowerCase()
            );

            const result = getSenderInfo(draftState.selectSenderFolder.originalRecords);
            draftState.selectSenderFolder = {
                ...draftState.selectSenderFolder,
                folderRecords: [...result],
            };
        }
        draftState.selectedDocuments = updatedSelectedList;
    },

    [Constants.MAILBOX_DOCUMENTS_WAIT_FOR_UPDATE]: (
        draftState,
        action: ReturnType<typeof MailboxActions.waitForUpdating>
    ) => {
        draftState.waitForUpdate = action.payload.wait;
    },

    [Constants.MAILBOX_DOCUMENTS_MOVE_FOR_UPDATE]: (
        draftState,
        action: ReturnType<typeof MailboxActions.clickForMoveUpdating>
    ) => {
        draftState.clickForMoveUpdatingState = action.payload.click;
    },

    [Constants.MAILBOX_DOCUMENT_ADD]: (draftState, action: ReturnType<typeof MailboxActions.addDocument>) => {
        draftState.documentList.push(action.payload.document);
    },

    [Constants.MAILBOX_ACCESS_UPDATE]: (draftState, action: ReturnType<typeof MailboxActions.updateAccess>) => {
        draftState.mailbox.access = action.payload.access;
    },

    [Constants.MAILBOX_USER_ROLES]: (draftState, action: ReturnType<typeof MailboxActions.setUserRoles>) => {
        draftState.userRoles = action.payload.userRoles;
    },

    [Constants.ADD_SELECTED_SENDER]: (draftState, action: ReturnType<typeof MailboxActions.setSelectedSenderName>) => {
        if (action.payload.SelectedSender == null) {
            draftState.selectedSender = null;
        } else {
            draftState.selectedSender = action.payload.SelectedSender;
            draftState.filteredDocumentList = draftState.selectSenderFolder.originalRecords.filter(
                (item) => item?.senderName?.toLowerCase() == draftState.selectedSender?.name?.toLowerCase()
            );
            draftState.isSenderClicked = action.payload.isSenderClicked;
        }
    },

    [Constants.MAILBOX_IS_GREENLANDIC_SUBSCRIPTION]: (
        draftState,
        action: ReturnType<typeof MailboxActions.setIsGreenlandSubscription>
    ) => {
        draftState.isGreenlandSubscription = action.payload.isGreenlandSubscription;
    },

    [Constants.MAILBOX_IS_READ_UNREAD_ACTION]: (
        draftState,
        action: ReturnType<typeof MailboxActions.setIsReadUnreadAction>
    ) => {
        draftState.isReadUnreadAction = action.payload.isReadUnreadAction;
    },
});
